import { RootActions, RootState } from "common/reducers";
import { receive } from "common/reducers/Utils";
import { Map, Record, List } from "immutable";
import {
  calcEstimateLineItemTotals,
  estimateLineItemFromJSON,
  EstimateLineItemRecord,
} from "common/records/EstimateLineItem";
import {
  RECEIVE_ESTIMATE_LINE_ITEM,
  DUPLICATE_ESTIMATE_LINE_ITEM,
  UPDATE_ESTIMATE_LINE_ITEM_TOTALS,
  DECREMENT_ESTIMATE_LINE_ITEM_UNSAVED_ID,
  SET_ESTIMATE_LINE_ITEM_BY_ID,
  EstimateLineItemActions,
  PUSH_ESTIMATE_LINE_ITEM_DOCUMENT_ID,
  PUSH_ESTIMATE_LINE_ITEM_IMAGE_ID,
  REMOVE_ESTIMATE_LINE_ITEM_DOCUMENT_ID,
  REMOVE_ESTIMATE_LINE_ITEM_IMAGE_ID,
  RESET_DUPLICATE_SOURCE_TO_CLONE_MAP,
} from "common/reducers/estimateLineItem.actions";
import { EstimateLineItemOptionActions } from "common/reducers/estimateLineItemOption.actions";
import { estimateLineItemOptionsReducer } from "common/reducers/estimateLineItemOption.reducer";
import { reducer as imageReducer } from "common/reducers/image.reducer";
import { reducer as documentReducer } from "common/reducers/document.reducer";
import { openingEstimationReducer } from "common/reducers/openingEstimation.reducer";
import { OpeningEstimationActions } from "common/reducers/openingEstimation.actions";
import { ImageActions } from "common/reducers/image.actions";
import { DocumentActions } from "common/reducers/document.actions";
import {
  estimateLineItem,
  unsavedId as estimateLineItemUnsavedIdSelector,
} from "common/selectors/estimateLineItem.selectors";
import { estimate } from "common/selectors/estimate.selectors";
import { estimateGroup } from "common/selectors/estimateGroup.selectors";
import { EstimateGroupRecord } from "common/records/EstimateGroup";
import { estimateLineItemOptions } from "common/selectors/estimateLineItemOption.selectors";
import { sumEstimateLineItemOptionTotals } from "common/records/EstimateLineItemOption";
import { DocumentRecord } from "common/records/Document";
import { ImageRecord } from "common/records/Image";

export const EstimateLineItemStateRecord = Record({
  byId: Map<number, EstimateLineItemRecord>(),
  lastSavedById: Map<number, EstimateLineItemRecord>(), // What's saved on the SERVER (for dirty tracking)
  editingSnapshotById: Map<number, EstimateLineItemRecord>(), // Snapshot for line item rollback
  unsavedId: -1,
  // Explicit "the open editing session is a brand-new draft" marker (the id of the draft created by
  // START_ADDING_LINE_ITEM), cleared on commit/cancel/reset. Draft-ness is tracked explicitly here
  // rather than inferred from the editing snapshot, which is scoped to the edited item.
  newDraftId: null as number | null,
  // Transient source→clone line-item id map emitted by the estimate-level duplicate cascade, read by
  // the attachment re-upload thunk (duplicateEstimateAttachments) to re-create owned documents on the
  // matching clone. (Owned images are cloned server-side via remote_file_url in the duplicate reducer,
  // not re-uploaded.) Reset at the start of each duplicate so it holds only the current cascade.
  duplicateSourceToCloneIdMap: Map<number, number>(),
});

export const initialState = EstimateLineItemStateRecord();

export const model = "estimateLineItems";

export const estimateLineItemsReducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    return state.set(model, initialState);
  }

  switch (action.type) {
    case RECEIVE_ESTIMATE_LINE_ITEM: {
      const lineItem = action.payload.estimateLineItem;

      lineItem?.options?.map((opt) => {
        state = estimateLineItemOptionsReducer(state, EstimateLineItemOptionActions.receiveEstimateLineItemOption(opt));
      });

      lineItem?.images?.map((img) => {
        state = imageReducer(state, ImageActions.receiveImage(img));
      });

      lineItem?.documents?.map((doc) => {
        state = documentReducer(state, DocumentActions.receiveDocument(doc));
      });

      lineItem?.opening_estimations?.map((oe) => {
        state = openingEstimationReducer(state, OpeningEstimationActions.receiveOpeningEstimation(oe));
      });

      let lineItemRecord = estimateLineItemFromJSON(lineItem);

      // After a save+upload flow, batchCreateDocument fires pushDocumentId to add the
      // newly minted positive doc id to this line item's document_ids. The route loader
      // that runs on navigate() then refetches the estimate; if the server response
      // arrives before the just-uploaded doc is visible to the read query (replica lag,
      // not-yet-committed, etc.), receiving a stale documents: [] would silently wipe
      // the positive ids out. Merge any positive-id docs/images present locally that
      // the response doesn't echo back, so a stale read can't erase confirmed uploads.
      // Soft-deleted records (_destroy: true) are excluded — the server confirmed the
      // delete by omitting them, so resurrecting their ids would un-delete them locally.
      // Records that are missing from byId entirely are also excluded — preserving them
      // would leave a dangling reference (id on the line item with no record), which
      // can happen when a task error handler removed the record but a prior render
      // captured the id, or any other source of inconsistency.
      const documentsById = state.getIn(["documents", "byId"]) as Map<number, DocumentRecord> | undefined;
      const existingDocIds = state.getIn([model, "byId", lineItem.id, "document_ids"]) as List<number> | undefined;
      if (existingDocIds && existingDocIds.size > 0) {
        const incomingDocIds = Array.isArray(lineItem?.documents)
          ? new Set(lineItem.documents.map((d: { id: number }) => d.id))
          : null;
        const preserved = existingDocIds.filter((id: number) => {
          if (id <= 0) return false;
          if (incomingDocIds?.has(id)) return false;
          const doc = documentsById?.get(id);
          return doc !== undefined && doc._destroy !== true;
        });
        if (preserved.size > 0) {
          const merged = (lineItemRecord.document_ids ?? List<number>()).concat(preserved) as List<number>;
          lineItemRecord = lineItemRecord.set("document_ids", merged);
        }
      }
      const imagesById = state.getIn(["images", "byId"]) as Map<number, ImageRecord> | undefined;
      const existingImgIds = state.getIn([model, "byId", lineItem.id, "image_ids"]) as List<number> | undefined;
      if (existingImgIds && existingImgIds.size > 0) {
        const incomingImgIds = Array.isArray(lineItem?.images)
          ? new Set(lineItem.images.map((i: { id: number }) => i.id))
          : null;
        const preserved = existingImgIds.filter((id: number) => {
          if (id <= 0) return false;
          if (incomingImgIds?.has(id)) return false;
          const img = imagesById?.get(id);
          return img !== undefined && img._destroy !== true;
        });
        if (preserved.size > 0) {
          const merged = (lineItemRecord.image_ids ?? List<number>()).concat(preserved) as List<number>;
          lineItemRecord = lineItemRecord.set("image_ids", merged);
        }
      }

      state = receive(state, model, lineItemRecord);

      // Copy to lastSavedById - represents what's saved on the SERVER
      state = state.setIn([model, "lastSavedById", lineItem.id], lineItemRecord);

      return state;
    }

    case UPDATE_ESTIMATE_LINE_ITEM_TOTALS: {
      const { estimateLineItemId } = action.payload;
      const lineItem = estimateLineItem(state, { estimateLineItemId });
      const estimateGroupRecord = estimateGroup(state, { estimateGroupId: lineItem?.estimate_group_id });
      const estimateRecord = estimate(state, { estimateId: estimateGroupRecord?.estimate_id });

      if (!lineItem || !estimateRecord || !estimateGroupRecord) {
        console.error("Missing required data in redux to calculate estimate line item totals", {
          estimateLineItemId,
          estimateLineItem: lineItem?.toJS(),
          estimateGroup: estimateGroupRecord?.toJS(),
          estimate: estimateRecord?.toJS(),
        });
        return state;
      }

      // Recalculate each option's stored prices before summing — percent-UOM options
      // derive their base_product_price from the line item's base price, so they must
      // be refreshed whenever the parent line item recalculates.
      lineItem.option_ids.forEach((optionId: number) => {
        state = estimateLineItemOptionsReducer(state, EstimateLineItemOptionActions.updateTotals(optionId));
      });

      const lineItemOptions = estimateLineItemOptions(state, {
        estimateLineItemOptionIds: lineItem?.option_ids,
        fullEstimate: true,
      });
      const optionTotals = sumEstimateLineItemOptionTotals(lineItemOptions, lineItem);
      const lineItemTotals = calcEstimateLineItemTotals(lineItem, estimateRecord);

      // `ext_price` is product-only (price × quantity, no options) — this is what rsf-api stores and
      // what pickLineItemFields persists. The option roll-up lives in the transient `ext_opt_price`
      // (= product-only ext_price + Σ non-excluded option ext), which group/estimate totals sum.
      // The whole transient family {ext_opt_price, option_labor_total, option_product_total} derives
      // from the single sumEstimateLineItemOptionTotals call above.
      const mergedTotals = {
        ...lineItemTotals,
        ext_opt_price: lineItemTotals.ext_price + optionTotals.subtotal,
        option_labor_total: optionTotals.labor_total,
        option_product_total: optionTotals.product_total,
      };

      return state.setIn([model, "byId", estimateLineItemId], lineItem.merge(mergedTotals));
    }

    case DECREMENT_ESTIMATE_LINE_ITEM_UNSAVED_ID: {
      const currentUnsavedId = state.getIn([model, "unsavedId"]) as number;
      return state.setIn([model, "unsavedId"], currentUnsavedId - 1);
    }

    case SET_ESTIMATE_LINE_ITEM_BY_ID: {
      const { estimateLineItemId, estimateLineItem: estimateLineItemData } = action.payload;
      const lineItemRecord = estimateLineItemFromJSON(estimateLineItemData);
      return state.setIn([model, "byId", estimateLineItemId], lineItemRecord);
    }

    case DUPLICATE_ESTIMATE_LINE_ITEM: {
      const { sourceLineItemId, targetGroupId } = action.payload;

      const originalLineItem = estimateLineItem(state, { estimateLineItemId: sourceLineItemId });
      if (!originalLineItem || originalLineItem._destroy) {
        return state;
      }

      const newLineItemId = estimateLineItemUnsavedIdSelector(state);
      state = estimateLineItemsReducer(state, EstimateLineItemActions.decrementUnsavedId());

      // Clone via Immutable .set() (not toJS()/setEstimateLineItemById) so every id-list field stays a
      // List — a toJS() round-trip leaves them plain arrays, which crashes transformLineItemForPayload's
      // .toArray() on save (BR-8). Product-derived references (product_image_*, estimate_doc_*,
      // contract_doc_*) carry over for free.
      //
      // Owned IMAGES are cloned synchronously below onto fresh negative ids that keep the source
      // `file` URL + `display` (server-side copy via `remote_file_url` on save — see
      // transformLineItemForPayload). Owned DOCUMENTS still reset to empty and re-create via client
      // re-upload (duplicateEstimateAttachments), and opening estimations re-duplicate via the cascade
      // below — so document_ids/opening_estimation_ids reset here (BR-9). image_ids is set to the cloned
      // negative ids further down.
      //
      // uuid MUST be regenerated: estimate_line_items has a unique uuid index server-side, the save
      // payload sends the line item's uuid (pickLineItemFields), and it's the client key the save
      // reconciles the server response by. Copying the source's uuid → "UUID has already been taken"
      // 422 on the clone POST. Mirrors how new / template-applied line items mint a fresh uuid.
      const duplicatedLineItem = originalLineItem
        .set("id", newLineItemId)
        .set("uuid", crypto.randomUUID())
        .set("estimate_group_id", targetGroupId)
        .set("option_ids", List<number>())
        .set("image_ids", List<number>())
        .set("document_ids", List<number>())
        .set("opening_estimation_ids", List<number>());
      state = state.setIn([model, "byId", newLineItemId], duplicatedLineItem);

      // Clone each owned (positive-id) image record onto a fresh NEGATIVE id, preserving the source
      // `file` URL and `display` (no `file_queue`). The save payload then emits a `remote_file_url`
      // nested attribute so the server copies the image server-side — no client download/re-upload for
      // images. Mirrors rsf-app's DUPLICATE_LINE_ITEM_IMAGES. The estimate is guaranteed clean-and-saved
      // at duplicate time (save-first toast), so every owned image has a persisted `file.url`.
      let imageUnsavedId = state.getIn(["images", "unsavedId"]) as number;
      let clonedImageIds = List<number>();
      (originalLineItem.image_ids ?? List<number>()).forEach((imageId: number) => {
        if (imageId <= 0) return;
        const sourceImage = state.getIn(["images", "byId", imageId]) as ImageRecord | undefined;
        if (!sourceImage || sourceImage._destroy) return;
        const clonedImage = sourceImage
          .set("id", imageUnsavedId)
          .set("file_queue", null)
          .set("imageable_id", newLineItemId);
        state = state.setIn(["images", "byId", imageUnsavedId], clonedImage);
        clonedImageIds = clonedImageIds.push(imageUnsavedId);
        imageUnsavedId -= 1;
      });
      if (clonedImageIds.size > 0) {
        state = state
          .setIn([model, "byId", newLineItemId, "image_ids"], clonedImageIds)
          .setIn(["images", "unsavedId"], imageUnsavedId);
      }

      // Record the source→clone mapping so the attachment re-upload thunk can re-create the source's
      // owned DOCUMENTS on this exact clone line item (images are handled synchronously above).
      state = state.setIn([model, "duplicateSourceToCloneIdMap", sourceLineItemId], newLineItemId);

      const targetGroupRecord = estimateGroup(state, { estimateGroupId: targetGroupId }) as EstimateGroupRecord;
      if (targetGroupRecord) {
        const updatedLineItemIds = targetGroupRecord.line_item_ids.push(newLineItemId);
        state = state.setIn(["estimateGroups", "byId", targetGroupId, "line_item_ids"], updatedLineItemIds);
      }

      originalLineItem.option_ids.forEach((optionId: number) => {
        state = estimateLineItemOptionsReducer(
          state,
          EstimateLineItemOptionActions.duplicateEstimateLineItemOption(optionId, newLineItemId),
        );
      });

      originalLineItem.opening_estimation_ids.forEach((openingEstimationId: number) => {
        state = openingEstimationReducer(
          state,
          OpeningEstimationActions.duplicateOpeningEstimation(openingEstimationId, newLineItemId),
        );
      });

      return state;
    }

    case RESET_DUPLICATE_SOURCE_TO_CLONE_MAP: {
      return state.setIn([model, "duplicateSourceToCloneIdMap"], Map<number, number>());
    }

    case PUSH_ESTIMATE_LINE_ITEM_DOCUMENT_ID: {
      const { estimateLineItemId, documentId } = action.payload;
      const lineItemRecord = state.getIn([model, "byId", estimateLineItemId]) as EstimateLineItemRecord | undefined;
      if (!lineItemRecord) {
        return state;
      }
      let nextState = state;
      const existingIds = lineItemRecord.document_ids ?? List<number>();
      if (!existingIds.includes(documentId)) {
        nextState = nextState.setIn([model, "byId", estimateLineItemId, "document_ids"], existingIds.push(documentId));
      }
      // A positive id is a server-confirmed record (createDirect persisted the link at upload time),
      // so it is already saved state — record it in the baseline too, or the estimate reads falsely
      // dirty after a single save until a second save (RSF-25794 BR-6). Negative (staged /
      // keep-on-failure) ids stay byId-only so pending and failed uploads still read dirty.
      if (documentId > 0) {
        const savedRecord = nextState.getIn([model, "lastSavedById", estimateLineItemId]) as
          | EstimateLineItemRecord
          | undefined;
        const savedIds = savedRecord?.document_ids ?? List<number>();
        if (savedRecord && !savedIds.includes(documentId)) {
          nextState = nextState.setIn(
            [model, "lastSavedById", estimateLineItemId, "document_ids"],
            savedIds.push(documentId),
          );
        }
      }
      return nextState;
    }

    case PUSH_ESTIMATE_LINE_ITEM_IMAGE_ID: {
      const { estimateLineItemId, imageId } = action.payload;
      const lineItemRecord = state.getIn([model, "byId", estimateLineItemId]) as EstimateLineItemRecord | undefined;
      if (!lineItemRecord) {
        return state;
      }
      let nextState = state;
      const existingIds = lineItemRecord.image_ids ?? List<number>();
      if (!existingIds.includes(imageId)) {
        nextState = nextState.setIn([model, "byId", estimateLineItemId, "image_ids"], existingIds.push(imageId));
      }
      // Positive ids are server-confirmed → also record in the saved baseline (same rule as
      // PUSH_ESTIMATE_LINE_ITEM_DOCUMENT_ID; RSF-25794 BR-6). Negative (staged / keep-on-failure)
      // ids stay byId-only.
      if (imageId > 0) {
        const savedRecord = nextState.getIn([model, "lastSavedById", estimateLineItemId]) as
          | EstimateLineItemRecord
          | undefined;
        const savedIds = savedRecord?.image_ids ?? List<number>();
        if (savedRecord && !savedIds.includes(imageId)) {
          nextState = nextState.setIn(
            [model, "lastSavedById", estimateLineItemId, "image_ids"],
            savedIds.push(imageId),
          );
        }
      }
      return nextState;
    }

    case REMOVE_ESTIMATE_LINE_ITEM_DOCUMENT_ID: {
      const { estimateLineItemId, documentId } = action.payload;
      const lineItemRecord = state.getIn([model, "byId", estimateLineItemId]) as EstimateLineItemRecord | undefined;
      if (!lineItemRecord) {
        return state;
      }
      const updatedIds = (lineItemRecord.document_ids ?? List<number>()).filter((id: number) => id !== documentId);
      return state.setIn([model, "byId", estimateLineItemId, "document_ids"], updatedIds);
    }

    case REMOVE_ESTIMATE_LINE_ITEM_IMAGE_ID: {
      const { estimateLineItemId, imageId } = action.payload;
      const lineItemRecord = state.getIn([model, "byId", estimateLineItemId]) as EstimateLineItemRecord | undefined;
      if (!lineItemRecord) {
        return state;
      }
      const updatedIds = (lineItemRecord.image_ids ?? List<number>()).filter((id: number) => id !== imageId);
      return state.setIn([model, "byId", estimateLineItemId, "image_ids"], updatedIds);
    }

    default:
      return state;
  }
};
